import { Link } from "react-router-dom";
import { ChevronRight, FileDown, FileSpreadsheet } from "lucide-react";
import { ProgressBar, ColorDot, colorLabel } from "./ProgressBar";

export default function ClassOverviewSection({ cls, pdfBusy, onDownloadPdf, xlsBusy, onDownloadExcel }) {
  return (
    <section className="bg-white border border-[var(--parchment-2)]" data-testid={`class-section-${cls.class.id}`}>
      <header className="flex items-baseline justify-between px-6 py-5 border-b border-[var(--parchment-2)]">
        <div>
          <div className="eyebrow">{cls.class.level}</div>
          <h2 className="font-serif-display text-3xl font-semibold mt-1">{cls.class.name}</h2>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <ColorDot color={cls.overall.color} />
          <span className="font-medium">{colorLabel(cls.overall.color)}</span>
          <span className="font-mono-spec text-[var(--muted)]">{cls.overall.ended}/{cls.overall.total} ch.</span>
          <button
            onClick={() => onDownloadPdf(cls.class)}
            disabled={pdfBusy === cls.class.id}
            className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5"
            data-testid={`pdf-btn-${cls.class.id}`}
          >
            <FileDown size={14} /> {pdfBusy === cls.class.id ? "Génération..." : "Rapport PDF"}
          </button>
          <button
            onClick={() => onDownloadExcel(cls.class)}
            disabled={xlsBusy === cls.class.id}
            className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5"
            title="Absences, retards et signalements par trimestre"
            data-testid={`xlsx-btn-${cls.class.id}`}
          >
            <FileSpreadsheet size={14} /> {xlsBusy === cls.class.id ? "Export..." : "Excel"}
          </button>
        </div>
      </header>

      <div className="divide-y divide-[var(--parchment-2)]">
        {cls.subjects.map((s) => <SubjectRowLink key={s.assignment_id} s={s} />)}
        {cls.subjects.length === 0 && (
          <div className="px-6 py-4 text-sm text-[var(--ink-soft)]">Aucune matière assignée.</div>
        )}
      </div>
    </section>
  );
}

function SubjectRowLink({ s }) {
  return (
    <Link
      to={`/admin/assignment/${s.assignment_id}`}
      className="group grid grid-cols-12 gap-4 items-center px-6 py-4 hover:bg-[var(--parchment)]/60 transition-colors"
      data-testid={`admin-subject-row-${s.assignment_id}`}
    >
      <div className="col-span-12 sm:col-span-3">
        <div className="font-serif-display text-lg font-medium">{s.subject.name}</div>
        <div className="text-xs text-[var(--ink-soft)] mt-0.5">{s.teacher.name}</div>
        {s.progress.total === 0 && (
          <span className="mt-1 inline-block px-2 py-0.5 bg-[var(--just)]/15 text-[var(--just)] text-xs" data-testid={`no-program-badge-${s.assignment_id}`}>
            Programme non saisi
          </span>
        )}
      </div>
      <div className="col-span-9 sm:col-span-7">
        <ProgressBar
          percent={s.progress.percent}
          expectedPercent={s.progress.expected_percent}
          color={s.progress.color}
          testId={`admin-progress-${s.assignment_id}`}
        />
        <div className="mt-2 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <ColorDot color={s.progress.color} />
            <span>{colorLabel(s.progress.color)}</span>
          </div>
          <span className="font-mono-spec text-[var(--muted)]">
            {s.progress.ended}/{s.progress.total} ch. · attendu {s.progress.expected}
          </span>
        </div>
      </div>
      <div className="col-span-3 sm:col-span-2 text-right">
        <ChevronRight className="inline text-[var(--muted)] group-hover:text-[var(--maroon)] transition-colors" />
      </div>
    </Link>
  );
}
